import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";

// Hooks
import { useAppSelector, useAppDispatch } from "app/hooks";
import useResizeObserver from "use-resize-observer";

// Store
import { getCharts, getLD12, LD12Chart, TImprovementQuestion } from "store/slice/charts";

// Enums
import { ChartIds, ChartRouteNames } from "enums/chartEnums";

// Utils
import { buildParamsString } from "utils";

// Charts
import { getChart, IChartConfig } from "components/Projection/ProjectionBody/chartsListConfig";
import { urlPaths } from "enums/urlPaths";
import { chartsRoutes } from "components/Layout/MainLayout/routeConfig";
import useChartSlice from "store/slice/charts/useChartSlice";

const useCharts = () => {
    const projectionChartsOrder = chartsRoutes
        .filter((config) => config.key !== ChartIds.Bridge)
        .map((config) => config.key);
    const projectionChartNamesOrder = chartsRoutes
        .filter((config) => config.key !== ChartIds.Bridge)
        .map((config) => config.route.toLowerCase());

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    const { companyId } = useParams();
    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");
    const searchAiId = searchParams.get("searchAiId");
    const briefId = searchParams.get("briefId");
    const queryParams = buildParamsString({ projectId, insightId, briefId, searchAiId });

    const { data: chartsData, status } = useAppSelector(getCharts);
    const isFetchingCharts = status === "fetching";
    const insightsSectionRef = useRef<HTMLDivElement>(null);
    const previousChart = useRef("");
    const { ref: chartWrapper, width = 0, height = 0 } = useResizeObserver<HTMLDivElement>();

    const [currentIndex, setIndex] = useState(0);
    const [reportChart, setReportChart] = useState<IChartConfig | undefined>();
    const [shouldShowPaceDefinitions, setShowPaceDefinitions] = useState(false);
    const projectionCharts = projectionChartsOrder.map((chart) => getChart(chart));
    const { chartName } = useParams();

    const errorData =
        chartsData?.graphs?.[projectionChartsOrder[currentIndex]].raw?.data_errors.filter((error) => error.message) ??
        [];
    const behaviouralBreakdownData = useChartSlice(getLD12) as LD12Chart;

    const handleOnClickInsightsLink = (): void => {
        if (insightsSectionRef && insightsSectionRef.current) {
            insightsSectionRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    const goToReport = (direction: "previous" | "next"): void => {
        const totalNumberOfReports = projectionChartsOrder.length;

        if (direction === "next") {
            if (currentIndex < totalNumberOfReports - 1) {
                setIndex((previousValue) => {
                    navigate(
                        `${urlPaths.Projection}/${companyId}/${
                            projectionChartNamesOrder[previousValue + 1]
                        }?${queryParams}`
                    );
                    return previousValue + 1;
                });
            } else {
                navigate(`${urlPaths.Projection}/${companyId}/${projectionChartNamesOrder[0]}?${queryParams}`);
                setIndex(0);
            }
        }

        if (direction === "previous") {
            if (currentIndex > 0) {
                setIndex((previousValue) => {
                    navigate(
                        `${urlPaths.Projection}/${companyId}/${
                            projectionChartNamesOrder[previousValue - 1]
                        }?${queryParams}`
                    );
                    return previousValue - 1;
                });
            } else {
                navigate(
                    `${urlPaths.Projection}/${companyId}/${
                        projectionChartNamesOrder[projectionChartsOrder.length - 1]
                    }?${queryParams}`
                );
                setIndex(projectionChartsOrder.length - 1);
            }
        }
    };

    const handleClickBtnViewReport = (question: TImprovementQuestion): void => {
        const chartIndex = projectionChartsOrder.findIndex(
            (chart) => chart.toLowerCase() === question.chartId.toLowerCase()
        );

        setIndex(chartIndex);
        if (window.innerWidth <= 1440) {
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        }

        navigate(`${urlPaths.Projection}/${companyId}/${projectionChartNamesOrder[chartIndex]}?${queryParams}`);
    };

    useEffect(() => {
        setReportChart(getChart(projectionChartsOrder[currentIndex]));
    }, [currentIndex, projectionChartsOrder]);

    useEffect(() => {
        if (chartName) {
            if (chartName !== ChartRouteNames.Bridge) {
                const index = projectionChartNamesOrder.findIndex((name) => name === chartName.toLowerCase());
                setIndex(index);
            } else {
                navigate(`${urlPaths.Projection}/${companyId}/${ChartRouteNames.Bridge}?${queryParams}`);
            }
            if (previousChart.current !== chartName) {
                previousChart.current = chartName;
                setShowPaceDefinitions(false);
            }
        } else {
            navigate(`${urlPaths.Projection}/${companyId}/${projectionChartNamesOrder[0]}?${queryParams}`);
            setIndex(0);
        }
        // projectionChartNamesOrder is causing multiple renders, it needs to be checked and fixed
    }, [chartName, dispatch, navigate, queryParams, shouldShowPaceDefinitions]);

    return {
        isFetchingCharts,
        width,
        height,
        reportChart,
        projectionCharts,
        shouldShowPaceDefinitions,
        insightsSectionRef,
        errorData,
        behaviouralBreakdownData,
        chartWrapper,
        handleOnClickInsightsLink,
        goToReport,
        handleClickBtnViewReport,
        setShowPaceDefinitions,
    };
};

export default useCharts;
